<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { useFormat } from '@/composables/useFormat'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'StockAdjustment',
  },
})

const router = useRouter()
const { request, loading } = useApiRequest()
const { formatRupiah, formatNumber } = useFormat()

// ─── State ────────────────────────────────────────────────────────────────────
const ingredients          = ref([])
const ingredientsLoading   = ref(false)
const ingredientSearch     = ref('')
const selectedIngredientId = ref(null)
const successMsg           = ref('')
const errors               = ref({})

const form = ref({
  quantity:  null,
  unit_cost: null,
  notes:     '',
})

// ─── Fetch Ingredients ────────────────────────────────────────────────────────
// Pakai EXACT pattern yang sama dengan ingredients/index.vue
const fetchIngredients = async (search = '') => {
  ingredientsLoading.value = true
  try {
    const query = { per_page: 100 }
    if (search) query.search = search

    const response = await useApi('/api/v1/ingredients', { query }).get()
    const data = response?.data ?? response

    ingredients.value = data?.data ?? []
  } catch (err) {
    console.error('fetchIngredients error:', err)
    ingredients.value = []
  } finally {
    ingredientsLoading.value = false
  }
}

// Debounce search — sama persis dengan pattern di project
let searchTimer
watch(ingredientSearch, (val) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchIngredients(val), 300)
})

onMounted(() => fetchIngredients())

// ─── Computed ─────────────────────────────────────────────────────────────────
const currentIngredient = computed(() =>
  ingredients.value.find(i => i.id === selectedIngredientId.value) ?? null
)

const previewStock = computed(() => {
  if (!currentIngredient.value || form.value.quantity === null || form.value.quantity === '') {
    return null
  }
  const base = parseFloat(currentIngredient.value.current_stock ?? 0)
  const adj  = parseFloat(form.value.quantity)
  if (isNaN(base) || isNaN(adj)) return null

  return base + adj
})

const previewIsNegative = computed(() =>
  previewStock.value !== null && previewStock.value < 0
)

const canSubmit = computed(() =>
  !!selectedIngredientId.value &&
  form.value.quantity !== null &&
  form.value.quantity !== 0 &&
  form.value.notes?.trim().length > 0
)

// ─── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  if (!canSubmit.value) return

  errors.value     = {}
  successMsg.value = ''

  await request(async () => {
    await useApi(`/api/v1/ingredients/${selectedIngredientId.value}/adjust`).post({
      body: {
        quantity:  form.value.quantity,
        unit_cost: form.value.unit_cost || undefined,
        notes:     form.value.notes,
      },
    })

    successMsg.value = `Stok ${currentIngredient.value?.name} berhasil diperbarui.`

    // Refresh list supaya stok terupdate di dropdown
    await fetchIngredients()

    setTimeout(() => {
      router.push({ name: 'inventory-stock-movements' })
    }, 1500)
  })
}

const resetForm = () => {
  form.value             = { quantity: null, unit_cost: null, notes: '' }
  selectedIngredientId.value = null
  errors.value           = {}
  successMsg.value       = ''
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center mb-6">
        <VBtn
          variant="text"
          icon="ri-arrow-left-line"
          class="me-2"
          :to="{ name: 'inventory-stock-movements' }"
        />
        <div>
          <h4 class="text-h4 font-weight-bold">Stock Adjustment</h4>
          <p class="text-body-2 text-medium-emphasis mt-1">Koreksi manual stok bahan baku</p>
        </div>
      </div>
    </VCol>

    <!-- Success -->
    <VCol v-if="successMsg" cols="12">
      <VAlert color="success" variant="tonal" density="compact" prepend-icon="ri-checkbox-circle-line">
        {{ successMsg }}
      </VAlert>
    </VCol>

    <VCol cols="12" md="8">
      <VCard>
        <VCardText class="pa-6">

          <!-- 1. Pilih Bahan Baku -->
          <p class="text-subtitle-1 font-weight-semibold mb-4">1. Pilih Bahan Baku</p>

          <VAutocomplete
            v-model="selectedIngredientId"
            v-model:search="ingredientSearch"
            :items="ingredients"
            :loading="ingredientsLoading"
            item-title="name"
            item-value="id"
            label="Bahan Baku *"
            placeholder="Ketik untuk mencari..."
            variant="outlined"
            clearable
            no-filter
            class="mb-2"
          >
            <template #item="{ props, item }">
              <VListItem v-bind="props">
                <template #subtitle>
                  Stok: {{ formatNumber(item.raw?.current_stock) }} {{ item.raw?.unit?.symbol ?? '' }}
                </template>
              </VListItem>
            </template>
          </VAutocomplete>

          <!-- Info stok saat ini -->
          <VAlert
            v-if="currentIngredient"
            color="info"
            variant="tonal"
            density="compact"
            class="mb-6"
          >
            <span class="me-4">
              <strong>Stok Saat Ini:</strong>
              {{ formatNumber(currentIngredient.current_stock) }} {{ currentIngredient.unit?.symbol }}
            </span>
            <span v-if="currentIngredient.average_cost">
              <strong>Harga Rata-rata:</strong>
              {{ formatRupiah(currentIngredient.average_cost) }}
            </span>
          </VAlert>

          <VDivider class="mb-6" />

          <!-- 2. Detail Adjustment -->
          <p class="text-subtitle-1 font-weight-semibold mb-4">2. Detail Adjustment</p>

          <VRow>
            <VCol cols="12" md="6">
              <VTextField
                v-model.number="form.quantity"
                label="Jumlah Adjustment *"
                type="number"
                variant="outlined"
                :suffix="currentIngredient?.unit?.symbol"
                :color="form.quantity > 0 ? 'success' : form.quantity < 0 ? 'error' : undefined"
                hint="Positif = tambah stok, Negatif = kurang stok"
                persistent-hint
              />
            </VCol>
            <VCol cols="12" md="6">
              <VTextField
                v-model.number="form.unit_cost"
                label="Harga Satuan (Opsional)"
                type="number"
                variant="outlined"
                prefix="Rp "
                hint="Kosongkan untuk pakai harga rata-rata"
                persistent-hint
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.notes"
                label="Alasan Adjustment *"
                placeholder="Contoh: Koreksi stok akibat tumpah, penggunaan untuk event internal, dll."
                rows="3"
                variant="outlined"
                counter="500"
                maxlength="500"
              />
            </VCol>
          </VRow>

          <!-- Preview stok akan jadi negatif -->
          <VAlert
            v-if="previewIsNegative"
            color="warning"
            variant="tonal"
            density="compact"
            class="mt-2 mb-4"
            prepend-icon="ri-error-warning-line"
          >
            Stok akan menjadi <strong>negatif ({{ formatNumber(previewStock) }})</strong>.
            Pastikan toko Anda mengizinkan stok negatif.
          </VAlert>

          <!-- Preview perubahan normal -->
          <VAlert
            v-else-if="previewStock !== null"
            :color="Number(form.quantity) >= 0 ? 'success' : 'error'"
            variant="tonal"
            density="compact"
            class="mt-2 mb-6"
          >
            <VIcon
              :icon="Number(form.quantity) >= 0 ? 'ri-arrow-up-circle-line' : 'ri-arrow-down-circle-line'"
              class="me-1"
            />
            Stok <strong>{{ currentIngredient?.name }}</strong> akan menjadi
            <strong>{{ formatNumber(previewStock) }} {{ currentIngredient?.unit?.symbol }}</strong>
          </VAlert>

          <!-- Actions -->
          <div class="d-flex gap-3 mt-4">
            <VBtn
              color="primary"
              :loading="loading"
              :disabled="!canSubmit || loading"
              prepend-icon="ri-save-line"
              @click="submit"
            >
              Simpan Adjustment
            </VBtn>
            <VBtn
              variant="tonal"
              color="secondary"
              :disabled="loading"
              @click="resetForm"
            >
              Reset
            </VBtn>
          </div>

        </VCardText>
      </VCard>
    </VCol>

    <!-- Info Panel kanan -->
    <VCol cols="12" md="4">
      <VCard color="warning" variant="tonal">
        <VCardText>
          <div class="d-flex align-center gap-2 mb-3">
            <VIcon icon="ri-alert-line" />
            <span class="font-weight-semibold">Perhatian</span>
          </div>
          <ul class="text-body-2 ps-4">
            <li class="mb-2">
              Adjustment langsung tercatat di ledger dan
              <strong>tidak dapat dihapus</strong>
            </li>
            <li class="mb-2">Gunakan angka <strong>positif</strong> untuk menambah stok</li>
            <li class="mb-2">Gunakan angka <strong>negatif</strong> untuk mengurangi stok</li>
            <li>Pastikan alasan adjustment jelas dan akurat</li>
          </ul>
        </VCardText>
      </VCard>

      <!-- Ringkasan bahan yang dipilih -->
      <VCard v-if="currentIngredient" class="mt-4" variant="outlined">
        <VCardText>
          <p class="text-subtitle-2 font-weight-semibold mb-3">Ringkasan Bahan Baku</p>
          <VList density="compact" lines="one">
            <VListItem>
              <template #prepend>
                <span class="text-caption text-medium-emphasis" style="min-width:90px">Nama</span>
              </template>
              <span class="text-body-2 font-weight-medium">{{ currentIngredient.name }}</span>
            </VListItem>
            <VListItem>
              <template #prepend>
                <span class="text-caption text-medium-emphasis" style="min-width:90px">Stok Saat Ini</span>
              </template>
              <span class="text-body-2 font-weight-medium">
                {{ formatNumber(currentIngredient.current_stock) }} {{ currentIngredient.unit?.symbol }}
              </span>
            </VListItem>
            <VListItem v-if="currentIngredient.minimum_stock">
              <template #prepend>
                <span class="text-caption text-medium-emphasis" style="min-width:90px">Stok Minimum</span>
              </template>
              <span class="text-body-2">
                {{ formatNumber(currentIngredient.minimum_stock) }} {{ currentIngredient.unit?.symbol }}
              </span>
            </VListItem>
            <VListItem>
              <template #prepend>
                <span class="text-caption text-medium-emphasis" style="min-width:90px">Harga Rata-rata</span>
              </template>
              <span class="text-body-2">{{ formatRupiah(currentIngredient.average_cost) }}</span>
            </VListItem>
          </VList>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
